










































































import {StairsIcon} from '@/icons';
import {FloorAreaParameter} from '@/model/api/parameters';
import {BuildingPartControlState} from '@/model/state/BuildingPartControlState';
import {RoomControlState} from '@/model/state/RoomControlState';
import {insertAfterKey, isPositiveNumber, parseDecimalNumber} from '@/utils';
import {cloneDeep, fromPairs, round} from 'lodash-es';
import {v4 as uuid} from 'uuid';
import {Mixins} from 'vue-property-decorator';
import {Component, Prop, Watch} from 'vue-property-decorator';
import BuildingPartControl from './BuildingPartControl.vue';
import Control from './Control';
import RoomControl from './RoomControl.vue';
import {BuildingPart} from './values';
import { RoomList } from './values';

type FloorAreaControlValue = FloorAreaParameter['value'];

@Component({
    components: {BuildingPartControl, RoomControl, StairsIcon},
    watch: {currentValue: 'updateValue'},
})
export default class FloorAreaControl extends Mixins<Control<FloorAreaControlValue>>(Control) {
    @Prop({type: Object, required: true})
    public readonly roomLists: { [listId: string]: RoomList };

    private get currentValue(): FloorAreaControlValue | null {
        if (! this.hasBuildingPartsSpecified()) {
            const grossArea = this.grossArea;
            const netArea = this.netArea;

            if (grossArea === null || netArea === null || grossArea < netArea) {
                return null;
            }

            return { gross: grossArea, net: netArea};
        }

        const buildingParts = Object.values(this.buildingParts).map(partState => partState.value);

        if (buildingParts.includes(null)) {
            return null;
        }

        return buildingParts as BuildingPart[];
    }

    public get grossArea(): number | null {
        if (this.grossAreaRaw === '') {
            return this.netArea;
        }

        const grossArea = parseDecimalNumber(this.grossAreaRaw);

        return isPositiveNumber(grossArea) ? grossArea : null;
    }

    public get netArea(): number | null {
        const netArea = parseDecimalNumber(this.netAreaRaw);

        return isPositiveNumber(netArea) ? netArea : null;
    }

    public grossAreaRaw: string = '';
    public netAreaRaw: string = '';

    public buildingParts: { [key: string]: BuildingPartControlState } = {};

   public  mounted(): void  {
        const value = this.value;

        if (Array.isArray(value)) {
            this.buildingParts = fromPairs(value.map(part => [uuid(), BuildingPartControlState.fromValue(part)]));
            return;
        }

        if (value !== null) {
            this.grossAreaRaw = '' + value.gross;
            this.netAreaRaw = '' + value.net;
        }
    }

    public addBuildingPart(): void {
       this.buildingParts = Object.assign({}, this.buildingParts, { [uuid()]: new BuildingPartControlState()});
    }

    public hasBuildingPartsSpecified(): boolean {
        return Object.keys(this.buildingParts).length > 0;
    }

    protected cloneBuildingPart(index: string): void {
        this.buildingParts = insertAfterKey(this.buildingParts, index, uuid(), cloneDeep(this.buildingParts[index]));
    }

    protected removeBuildingPart(index: string): void {
       const buildingParts = Object.assign({}, this.buildingParts);

       delete buildingParts[index];

       this.buildingParts = buildingParts;
    }

    protected isGrossAreaTooLow(): boolean {
       const grossArea = this.grossArea;
       const netArea = this.netArea;

       return grossArea !== null && netArea !== null && grossArea < netArea;
    }

    protected getValue(): FloorAreaControlValue | null {
        return this.currentValue;
    }

    protected validateCurrentValue(): boolean {
        return this.currentValue !== null;
    }

    @Watch('buildingParts', { deep: true })
    private buildingPartChanged(): void {
        if (! this.hasBuildingPartsSpecified()) {
            this.grossAreaRaw = this.netAreaRaw = '';
            return;
        }

        const rooms = Object.values(this.buildingParts)
            .reduce((accumulator: RoomControlState[], part) => [...accumulator, ...Object.values(part.rooms)], []);

        this.grossAreaRaw = this.calculateArea(rooms);
        this.netAreaRaw = this.calculateArea(rooms.filter((r) => ! r.isAddition));
    }

    private calculateArea(rooms: RoomControlState[]): string  {
       return '' + round(rooms.reduce((area, room) => area + room.area, 0), 2);
    }
}
